import React, { useEffect, useMemo, useState, useRef, useLayoutEffect } from "react";
import { Chip, CircularProgress, MenuItem, TextField, Button } from "@mui/material";
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import SelectAllIcon from '@mui/icons-material/SelectAll';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { v4 as uuidv4 } from 'uuid';
import { API_BASE_URL } from "../../../config";
import "./DatasetBuilder.scss";
import ExportDialog from "./ExportDialog";
import { PivotRow } from "./types";
import { VETSTAT_UI } from "../../strings";

interface PivotTableProps {
  title: string;
  description: string;
  rowFields: string[];
  colField?: string;
  rowLabel: string;
  colLabel: string;
  showSpeciesFilter?: boolean;
  showAgeGroupFilter?: boolean;
  showTotalColumn?: boolean;
  hideSubtotalValues?: boolean;
  precision?: number;
}

const PivotTable: React.FC<PivotTableProps> = ({
  title,
  description,
  rowFields,
  colField = "new_uk_ema",
  rowLabel,
  colLabel,
  showSpeciesFilter = false,
  showAgeGroupFilter = false,
  showTotalColumn = false,
  hideSubtotalValues = false,
  precision = 1
}) => {
  const tableRef = useRef<HTMLTableElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [openExportDialog, setOpenExportDialog] = useState(false);
  const [layout, setLayout] = useState<'original' | 'a4-full' | 'a4-half'>('original');

  const fixRotationOnClone = (clonedDoc: Document) => {
      const divs = clonedDoc.querySelectorAll<HTMLElement>('.rotated-headers th div');
      let maxLabelWidth = 0;
      if (divs.length > 0) {
          divs.forEach(div => {
              const span = div.querySelector('span') as HTMLElement;
              if (span) {
                  span.style.writingMode = 'horizontal-tb';
                  span.style.display = 'inline-block';
                  span.style.whiteSpace = 'nowrap';
                  const estimated = (span.textContent?.length || 0) * 9; 
                  const actual = span.scrollWidth;
                  const width = actual > 0 ? actual : estimated;
                  if (width > maxLabelWidth) maxLabelWidth = width;
              }
          });
          const newHeight = Math.max(220, maxLabelWidth + 50);
          const ths = clonedDoc.querySelectorAll<HTMLElement>('.rotated-headers th');
          ths.forEach(th => { th.style.height = `${newHeight}px`; });
          divs.forEach(div => {
              div.style.position = 'relative';
              div.style.height = '100%';
              const span = div.querySelector('span') as HTMLElement;
              if (span) {
                  span.style.position = 'absolute';
                  span.style.left = '50%';
                  span.style.bottom = '10px';
                  span.style.transformOrigin = 'left center';
                  span.style.transform = 'rotate(-90deg)';
                  span.style.width = 'max-content';
                  span.style.display = 'block';
                  span.style.whiteSpace = 'nowrap';
                  span.style.writingMode = 'horizontal-tb';
              }
          });
      }
  };

  const handleExportImage = async (type: 'png' | 'jpeg') => {
    if (!tableRef.current) return;
    try {
        const canvas = await html2canvas(tableRef.current, { 
            scale: 2,
            onclone: (clonedDoc) => {
                const table = clonedDoc.querySelector('table');
                if (table) {
                    table.style.transform = 'none';
                    table.style.transformOrigin = '0 0';
                }
                fixRotationOnClone(clonedDoc);
            }
        });
        if (type === 'jpeg') {
            const context = canvas.getContext('2d');
            if (context) {
                const data = context.getImageData(0, 0, canvas.width, canvas.height);
                context.globalCompositeOperation = "destination-over";
                context.fillStyle = "#ffffff";
                context.fillRect(0, 0, canvas.width, canvas.height);
                context.globalCompositeOperation = "source-over";
            }
        }
        const imgData = canvas.toDataURL(`image/${type}`);
        const link = document.createElement("a");
        link.href = imgData; link.download = `dataset_builder.${type}`; link.click();
    } catch (e) {
        console.error("Image Export failed", e);
      alert(VETSTAT_UI.datasetBuilder.imageExportFailed);
    }
  };

  const handleExportPdf = async () => {
    if (!tableRef.current) return;
    const exportId = uuidv4();
    tableRef.current.dataset.exportId = exportId;
    
    // Store split points (DOM Y-coordinates relative to table top)
    const splitPoints: number[] = [];
    let headerHeightDOM = 0;
    let tableHeightDOM = 0;

    try {
        const canvas = await html2canvas(tableRef.current, {
            scale: 2,
            onclone: (clonedDoc) => {
                let table = clonedDoc.querySelector(`table[data-export-id="${exportId}"]`) as HTMLElement;
                if (!table) {
                    table = clonedDoc.querySelector('table') as HTMLElement;
                }

                if (layout !== 'original' && table) {
                    table.style.transform = 'none';
                    table.style.transformOrigin = '0 0';
                    const widthPx = layout === 'a4-full' ? '700px' : '350px';
                    table.style.width = widthPx;
                    table.style.minWidth = '0';
                    table.style.tableLayout = 'auto';
                }
                
                fixRotationOnClone(clonedDoc);

                // --- Smart Page Break Calculation ---
                if (table) {
                    const thead = table.querySelector('thead');
                    const tbody = table.querySelector('tbody');
                    const rows = tbody ? Array.from(tbody.querySelectorAll('tr')) : [];

                    if (thead && rows.length > 0) {
                        const tableRect = table.getBoundingClientRect();
                        const theadRect = thead.getBoundingClientRect();
                        headerHeightDOM = theadRect.height;
                        tableHeightDOM = tableRect.height;
                        
                        // Calculate available height for body content on a page (in DOM px)
                        // PDF A4: 595.28 x 841.89 pt
                        // Margins: 40pt top/bottom -> 80pt total
                        const PAGE_HEIGHT_PT = 841.89;
                        const MARGIN_PT = 40;
                        const AVAILABLE_HEIGHT_PT = PAGE_HEIGHT_PT - (MARGIN_PT * 2);
                        
                        // Determine scale ratio (Points per DOM Pixel)
                        let targetWidthPt = 595.28 - (MARGIN_PT * 2); // Default A4 Portrait Width available
                        if (layout === 'a4-half') targetWidthPt /= 2;
                        else if (layout === 'original') {
                             const domWidth = tableRect.width;
                             if (domWidth > targetWidthPt && domWidth < 841.89 - 80) {
                                 targetWidthPt = 841.89 - 80;
                             }
                        }

                        const domWidth = tableRect.width;
                        const ratio = targetWidthPt / domWidth; // pt / px
                        
                        const fullPageBodyHeightDOM = AVAILABLE_HEIGHT_PT / ratio;
                        // First page has header, subsequent pages have full height
                        let currentPageBodyHeightDOM = fullPageBodyHeightDOM - headerHeightDOM;
                        
                        let currentY = 0; // Filled height on current page
                        let i = 0;
                        
                        while (i < rows.length) {
                            const row = rows[i];
                            const rowRect = row.getBoundingClientRect();
                            const h = rowRect.height;
                            const rowRelativeTop = rowRect.top - tableRect.top;

                            // Special check for SuperRow (Species) to keep it with first Age Group
                            if (row.classList.contains('superRow')) {
                                let combinedHeight = h;
                                let k = i + 1;
                                // Look for first midRow and its subRows
                                if (k < rows.length && rows[k].classList.contains('midRow')) {
                                    combinedHeight += rows[k].getBoundingClientRect().height;
                                    k++;
                                    while (k < rows.length && rows[k].classList.contains('subRow')) {
                                        combinedHeight += rows[k].getBoundingClientRect().height;
                                        k++;
                                    }
                                    
                                    // If this combined block doesn't fit on current page, but fits on new page
                                    // Note: "new page" usually means subsequent page (no header)
                                    // But if we are currently on page 1, next page has more room.
                                    // If we are on page 2, next page is also page 2-like.
                                    // The check logic: compare against `currentPageBodyHeightDOM`.
                                    // If we split, we move to a page with `fullPageBodyHeightDOM`.
                                    
                                    if (currentY + combinedHeight > currentPageBodyHeightDOM) {
                                        // Does it fit on the next page (which has full height)?
                                        if (combinedHeight <= fullPageBodyHeightDOM) {
                                             splitPoints.push(rowRelativeTop);
                                             currentY = 0;
                                             // Update available height for the new page (no header)
                                             currentPageBodyHeightDOM = fullPageBodyHeightDOM;
                                        }
                                    }
                                }
                            }
                            
                            // Determine logical block
                            let blockHeight = h;
                            let blockEndIndex = i;
                            
                            // Check for MidGroup (midRow + subRows)
                            if (row.classList.contains('midRow')) {
                                let j = i + 1;
                                while (j < rows.length && rows[j].classList.contains('subRow')) {
                                    blockHeight += rows[j].getBoundingClientRect().height;
                                    blockEndIndex = j;
                                    j++;
                                }
                            }
                            
                            // Decision
                            if (currentY + blockHeight <= currentPageBodyHeightDOM) {
                                // Fits
                                currentY += blockHeight;
                                i = blockEndIndex + 1;
                            } else {
                                // Doesn't fit.
                                // Can it fit on a fresh page (full height)?
                                if (blockHeight <= fullPageBodyHeightDOM) {
                                    // Yes, push to next page
                                    const cutY = rowRect.top - tableRect.top; // Relative to table top
                                    splitPoints.push(cutY);
                                    currentY = blockHeight; // Reset and add block
                                    // Update available height for the new page (no header)
                                    currentPageBodyHeightDOM = fullPageBodyHeightDOM;
                                    i = blockEndIndex + 1;
                                } else {
                                    // Too big for a single page, must split internally.
                                    // Check if even the single row fits
                                    if (currentY + h > currentPageBodyHeightDOM) {
                                         const cutY = rowRect.top - tableRect.top;
                                         splitPoints.push(cutY);
                                         currentY = h;
                                         currentPageBodyHeightDOM = fullPageBodyHeightDOM;
                                    } else {
                                         currentY += h;
                                    }
                                    i++; 
                                }
                            }
                        }
                    }
                }
            }
        });

        const pdf = new jsPDF({ orientation: 'p', unit: "pt", format: 'a4' });
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const margin = 40;
        const availableWidth = pageWidth - (margin * 2);
        
        let targetWidth = availableWidth;
        if (layout === 'a4-half') targetWidth = availableWidth / 2;
        
        const ratio = targetWidth / canvas.width;
        
        // Canvas pixel coordinates
        const scale = 2; // html2canvas scale
        const headerHeightCanvas = headerHeightDOM * scale;
        
        let lastCutY = headerHeightDOM; // Start after header (we draw header separately)
        
        // Helper to draw a page
        const drawPage = (startDOM: number, endDOM: number, isFirst: boolean) => {
            if (!isFirst) pdf.addPage();
            
            let contentYPt = margin;

            // 1. Draw Header (Only on First Page)
            if (isFirst) {
                const headerCanvasHeight = headerHeightDOM * scale;
                const tempHeader = document.createElement('canvas');
                tempHeader.width = canvas.width;
                tempHeader.height = headerCanvasHeight;
                const ctxH = tempHeader.getContext('2d');
                if (ctxH) {
                    ctxH.drawImage(canvas, 0, 0, canvas.width, headerCanvasHeight, 0, 0, tempHeader.width, tempHeader.height);
                    const headerImg = tempHeader.toDataURL("image/png");
                    const headerHeightPt = headerHeightDOM * ratio * scale;
                    pdf.addImage(headerImg, "PNG", margin, margin, targetWidth, headerHeightPt); 
                    contentYPt += headerHeightPt;
                }
            }
            
            // 2. Draw Body Slice
            const sliceTopCanvas = startDOM * scale;
            const sliceHeightCanvas = (endDOM - startDOM) * scale;
            
            if (sliceHeightCanvas > 0) {
                const tempBody = document.createElement('canvas');
                tempBody.width = canvas.width;
                tempBody.height = sliceHeightCanvas;
                const ctxB = tempBody.getContext('2d');
                if (ctxB) {
                    ctxB.drawImage(canvas, 0, sliceTopCanvas, canvas.width, sliceHeightCanvas, 0, 0, tempBody.width, tempBody.height);
                    const bodyImg = tempBody.toDataURL("image/png");
                    pdf.addImage(bodyImg, "PNG", margin, contentYPt, targetWidth, sliceHeightCanvas * ratio);
                }
            }
        };

        // Process splits
        let currentStart = headerHeightDOM; // Skip header initially
        
        if (splitPoints.length === 0) {
             drawPage(currentStart, tableHeightDOM, true);
        } else {
            // Segments
            for (let i = 0; i < splitPoints.length; i++) {
                drawPage(currentStart, splitPoints[i], i === 0);
                currentStart = splitPoints[i];
            }
            // Last segment
            drawPage(currentStart, tableHeightDOM, false);
        }

        pdf.save("dataset_builder.pdf");
    } catch (e) {
        console.error("PDF Export failed", e);
      alert(VETSTAT_UI.datasetBuilder.pdfExportFailed);
    } finally {
        if (tableRef.current) delete tableRef.current.dataset.exportId;
    }
  };

  const [rows, setRows] = useState<PivotRow[]>([]);
  const [columns, setColumns] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [availableColumns, setAvailableColumns] = useState<string[]>([]);
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [selectedYears, setSelectedYears] = useState<string[]>([]);
  const [aggregationYear, setAggregationYear] = useState<string>("");
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [rotateHeaders, setRotateHeaders] = useState(false);
  const [availableSpecies, setAvailableSpecies] = useState<string[]>([]);
  const [speciesFilter, setSpeciesFilter] = useState<string>("");
  const [availableAgeGroups, setAvailableAgeGroups] = useState<string[]>([]);
  const [ageGroupFilter, setAgeGroupFilter] = useState<string>("");

  useLayoutEffect(() => {
    if (layout === 'original' || !tableRef.current || !wrapperRef.current) {
        if (tableRef.current) {
            tableRef.current.style.width = '';
            tableRef.current.style.transform = '';
            tableRef.current.style.transformOrigin = '';
        }
        return;
    }
    const table = tableRef.current;
    const wrapper = wrapperRef.current;
    table.style.width = 'max-content';
    table.style.transform = 'none';
    table.style.transformOrigin = 'top left';
    const wrapperStyle = window.getComputedStyle(wrapper);
    const paddingLeft = parseFloat(wrapperStyle.paddingLeft) || 0;
    const paddingRight = parseFloat(wrapperStyle.paddingRight) || 0;
    const availableWidth = wrapper.clientWidth - paddingLeft - paddingRight;
    const targetWidth = layout === 'a4-half' ? availableWidth * 0.5 : availableWidth;
    const maxContentWidth = table.scrollWidth;
    if (maxContentWidth <= targetWidth) {
      // Avoid upscaling, which can distort layout in A4 fit modes.
      table.style.transform = 'none';
    } else {
        table.style.width = `${targetWidth}px`;
        const wrappedWidth = table.scrollWidth;
        if (wrappedWidth > targetWidth) {
            const scale = targetWidth / wrappedWidth;
            table.style.transform = `scale(${scale})`;
        } else {
            table.style.transform = 'none';
        }
    }
  }, [layout, rows, columns, selectedColumns, selectedRows, rotateHeaders]);

  const baseURL = API_BASE_URL || "";

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true); setError(null);
    const params = new URLSearchParams();
    rowFields.forEach((field) => params.append("row_fields", field));
    params.append("col_field", colField);
    selectedColumns.forEach((value) => params.append("col_values", value));
    selectedYears.forEach((value) => params.append("years", value));
    if (aggregationYear) params.append("aggregation_year", aggregationYear);
    if (showSpeciesFilter && speciesFilter) params.append("species_filter", speciesFilter);
    if (showAgeGroupFilter && ageGroupFilter) params.append("age_group_filter", ageGroupFilter);

    fetch(`${baseURL}/api/vetstat/dataset_builder?${params.toString()}`, { signal: controller.signal })
      .then(async (res) => {
        const payload = await res.json();
        if (!res.ok) throw new Error(payload?.detail ?? VETSTAT_UI.datasetBuilder.pivotFetchFailed);
        return payload;
      })
      .then((payload) => {
        setAvailableColumns(payload.available_columns ?? payload.columns ?? []);
        setAvailableYears(payload.available_years ?? []);
        setAvailableSpecies(payload.available_species ?? []);
        setAvailableAgeGroups(payload.available_age_groups ?? []);
        setColumns(payload.columns ?? []);
        setRows(payload.rows ?? []);
      })
      .catch((err) => { if (err instanceof Error && err.name !== "AbortError") setError(err.message); })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [baseURL, rowFields, colField, selectedColumns, selectedYears, aggregationYear, speciesFilter, showSpeciesFilter, ageGroupFilter, showAgeGroupFilter]);

  const sortedColumns = useMemo(() => {
    const cols = [...columns];
    if (showTotalColumn && cols.length > 0) cols.push("Total");
    return cols;
  }, [columns, showTotalColumn]);

  const rowOptions = useMemo(() => {
    const speciesMap = new Map<string, string[]>();
    rows.forEach((row) => {
      const species = row.row_values[0];
      const ageGroup = row.row_values[1];
      if (species && species !== "Total") {
        if (!speciesMap.has(species)) speciesMap.set(species, []);
        if (ageGroup && !speciesMap.get(species)!.includes(ageGroup)) {
          speciesMap.get(species)!.push(ageGroup);
        }
      }
    });
    return Array.from(speciesMap.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([species, ageGroups]) => ({
        species,
        ageGroups: ageGroups.sort((a, b) => a.localeCompare(b))
      }));
  }, [rows]);

  const groupedRows = useMemo(() => {
    const hasColumns = selectedColumns.length > 0 || selectedYears.length > 0;
    if (!hasColumns) return [];
    const baseRows = rows.filter(row => {
        const species = row.row_values[0];
        const ageGroup = row.row_values[1];
        if (species === "Total") return false;
        return selectedRows.includes(species) || selectedRows.includes(ageGroup);
    });
    if (baseRows.length === 0) return [];

    const processedRows = showTotalColumn 
        ? baseRows.map(row => {
            let sum = 0;
            columns.forEach(col => { sum += (row.cells[col] ?? 0); });
            return { ...row, cells: { ...row.cells, "Total": sum } };
        })
        : baseRows;

    const groups: Record<string, {
      totals: Record<string, number>;
      midGroups: Record<string, {
        totals: Record<string, number>;
        subRows: PivotRow[];
      }>;
    }> = {};

    processedRows.forEach((row) => {
      const species = row.row_values[0] ?? "(ukendt)";
      const midLabel = rowFields.length > 2 ? (row.row_values[1] ?? "(ukendt)") : "";
      groups[species] ??= { totals: {}, midGroups: {} };
      groups[species].midGroups[midLabel] ??= { totals: {}, subRows: [] };
      groups[species].midGroups[midLabel].subRows.push(row);
      sortedColumns.forEach((col) => {
        const val = row.cells[col] ?? 0;
        groups[species].totals[col] = (groups[species].totals[col] ?? 0) + val;
        if (rowFields.length > 2) {
          groups[species].midGroups[midLabel].totals[col] = (groups[species].midGroups[midLabel].totals[col] ?? 0) + val;
        }
      });
    });

    const result = Object.entries(groups)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([species, payload]) => ({
        species,
        totals: payload.totals,
        midGroups: Object.entries(payload.midGroups)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([midLabel, midPayload]) => ({
            midLabel,
            totals: midPayload.totals,
            subRows: midPayload.subRows
          }))
      }));

    if (result.length > 0) {
        const grandTotals: Record<string, number> = {};
        result.forEach(group => {
            sortedColumns.forEach(col => { grandTotals[col] = (grandTotals[col] ?? 0) + (group.totals[col] ?? 0); });
        });
        result.push({ species: "Total", totals: grandTotals, midGroups: [] });
    }
    return result;
  }, [rows, selectedRows, selectedYears, selectedColumns, sortedColumns, showTotalColumn, columns, rowFields]);

  const isThreeLevel = rowFields.length > 2;

  return ( 
    <div className="pivotTableSection">
      <header className="datasetBuilderHeader">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h1>{title}</h1>
          <Button variant="outlined" startIcon={<RotateLeftIcon />} onClick={() => setRotateHeaders(!rotateHeaders)} size="small">Rotate Labels</Button>
        </div>
        <p>{description}</p>
      </header>

      <section className="datasetBuilderControls">
        {showSpeciesFilter && (
          <TextField select variant="outlined" size="small" label={VETSTAT_UI.datasetBuilder.selectSpeciesFilter} value={speciesFilter} onChange={(e) => setSpeciesFilter(e.target.value)} className="rowSelect" style={{ minWidth: '300px' }}>
            <MenuItem value=""><em>{VETSTAT_UI.datasetBuilder.noneSelected}</em></MenuItem>
                {availableSpecies.map((option) => (<MenuItem key={option} value={option}>{option}</MenuItem>))}
            </TextField>
        )}
        {showAgeGroupFilter && (
          <TextField select variant="outlined" size="small" label={VETSTAT_UI.datasetBuilder.selectAgeGroupFilter} value={ageGroupFilter} onChange={(e) => setAgeGroupFilter(e.target.value)} className="rowSelect" style={{ minWidth: '300px' }}>
            <MenuItem value=""><em>{VETSTAT_UI.datasetBuilder.noneSelected}</em></MenuItem>
                {availableAgeGroups.map((option) => (<MenuItem key={option} value={option}>{option}</MenuItem>))}
            </TextField>
        )}

        <TextField
          select variant="outlined" size="small" label={rowLabel} value={selectedRows}
          onChange={(event) => {
            const value = event.target.value as string[] | string;
            const newSelection = Array.isArray(value) ? value : String(value).split(",");
            const added = newSelection.filter(x => !selectedRows.includes(x));
            const removed = selectedRows.filter(x => !newSelection.includes(x));
            let finalSelection = [...newSelection];
            if (added.length > 0) {
              const group = rowOptions.find(g => g.species === added[0]);
              if (group) finalSelection = Array.from(new Set([...finalSelection, ...group.ageGroups]));
            }
            if (removed.length > 0) {
              const group = rowOptions.find(g => g.species === removed[0]);
              if (group) {
                const children = group.ageGroups;
                finalSelection = finalSelection.filter(x => !children.includes(x) && x !== removed[0]);
              }
            }
            setSelectedRows(finalSelection);
          }}
          SelectProps={{
            multiple: true,
            renderValue: (selected) => (
              <div className="chipsContainer">
                {(selected as string[]).map((value) => (<Chip key={value} label={value} size="small" style={{ marginRight: 4, marginBottom: 4 }} />))}
              </div>
            ),
          }}
          className="rowSelect"
        >
          {rowOptions.map((group) => [
            <MenuItem key={group.species} value={group.species} style={{ fontWeight: 'bold' }}>{group.species}</MenuItem>,
            ...group.ageGroups.map((ageGroup) => (
              <MenuItem key={`${group.species}-${ageGroup}`} value={ageGroup} style={{ paddingLeft: '2rem' }}>{ageGroup}</MenuItem>
            )),
          ]).flat()}
        </TextField>

        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", flex: 1, minWidth: "300px" }}>
          <TextField
            select variant="outlined" size="small" label={colLabel} value={selectedColumns}
            onChange={(event) => {
              const value = event.target.value as string[] | string;
              setSelectedColumns(Array.isArray(value) ? value : String(value).split(","));
            }}
            SelectProps={{
              multiple: true,
              renderValue: (selected) => (
                <div className="chipsContainer">
                  {(selected as string[]).map((value) => (<Chip key={value} label={value} size="small" style={{ marginRight: 4, marginBottom: 4 }} />))}
                </div>
              ),
            }}
            style={{ width: "100%" }}
          >
            {availableColumns.map((option) => (<MenuItem key={option} value={option}>{option}</MenuItem>))}
          </TextField>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <Button size="small" startIcon={<SelectAllIcon />} onClick={() => setSelectedColumns(availableColumns)} disabled={availableColumns.length === 0}>Select All</Button>
            <Button size="small" startIcon={<ClearAllIcon />} onClick={() => setSelectedColumns([])} disabled={selectedColumns.length === 0}>Deselect All</Button>
          </div>
        </div>

        {rowFields[0] !== "aar" && (
          <TextField
            select variant="outlined" size="small" label={VETSTAT_UI.datasetBuilder.selectYearsOptional} value={selectedYears}
            onChange={(event) => {
              const value = event.target.value as string[] | string;
              setSelectedYears(Array.isArray(value) ? value : String(value).split(","));
            }}
            SelectProps={{
              multiple: true,
              renderValue: (selected) => (
                <div className="chipsContainer">
                  {(selected as string[]).map((value) => (<Chip key={value} label={value} size="small" style={{ marginRight: 4, marginBottom: 4 }} />))}
                </div>
              ),
            }}
            className="rowSelect"
          >
            {availableYears.map((year) => (<MenuItem key={year} value={String(year)}>{year}</MenuItem>))}
          </TextField>
        )}

        {selectedYears.length > 1 && !rowFields.includes("aar") && (
          <TextField select variant="outlined" size="small" label={VETSTAT_UI.datasetBuilder.aggregationYear} value={aggregationYear} onChange={(e) => setAggregationYear(e.target.value)} className="rowSelect" style={{ minWidth: '200px' }}>
            <MenuItem value=""><em>{VETSTAT_UI.datasetBuilder.allSelectedYears}</em></MenuItem>
            {selectedYears.map((year) => (<MenuItem key={year} value={year}>{year}</MenuItem>))}
          </TextField>
        )}
      </section>

      <section className="datasetBuilderBody">
        {loading && <div className="status"><CircularProgress size={20} /> {VETSTAT_UI.datasetBuilder.loadingData}</div>}
        {error && <div className="status status--error">{error}</div>}
        {!loading && !error && (
          <div className="datasetBuilderActions" style={{ gap: '1rem', alignItems: 'center' }}>
           <TextField select variant="outlined" size="small" label={VETSTAT_UI.datasetBuilder.layout} value={layout} onChange={(e) => setLayout(e.target.value as any)} style={{ minWidth: '200px' }}>
             <MenuItem value="original">{VETSTAT_UI.datasetBuilder.layoutFitOriginal}</MenuItem>
             <MenuItem value="a4-full">{VETSTAT_UI.datasetBuilder.layoutA4Full}</MenuItem>
             <MenuItem value="a4-half">{VETSTAT_UI.datasetBuilder.layoutA4Half}</MenuItem>
            </TextField>

            <button
              className="exportButton" type="button" disabled={selectedColumns.length === 0 && selectedYears.length === 0}
              onClick={() => {
                const csvRows: string[] = [];
                csvRows.push([rowLabel, isThreeLevel ? VETSTAT_UI.datasetBuilder.csvHeaderAgeGroup : "", isThreeLevel ? VETSTAT_UI.datasetBuilder.csvHeaderYear : "", ...sortedColumns].filter(Boolean).join(";"));
                groupedRows.forEach((group) => {
                  csvRows.push([
                    group.species,
                    isThreeLevel ? "Total" : "",
                    isThreeLevel ? "" : "",
                    ...sortedColumns.map((col) => hideSubtotalValues ? "" : (group.totals[col] ?? 0).toFixed(precision))
                  ].join(";"));
                  group.midGroups.forEach((mid) => {
                    if (isThreeLevel) {
                      csvRows.push([
                        "",
                        mid.midLabel,
                        "Total",
                        ...sortedColumns.map((col) => hideSubtotalValues ? "" : (mid.totals[col] ?? 0).toFixed(precision))
                      ].join(";"));
                    }
                    mid.subRows.forEach(row => {
                      csvRows.push(["", isThreeLevel ? mid.midLabel : row.row_values[1], isThreeLevel ? row.row_values[2] : "", ...sortedColumns.map(col => (row.cells[col] ?? 0).toFixed(precision))].join(";"));
                    });
                  });
                });
                const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
                const url = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url; link.download = "dataset_builder.csv"; link.click(); URL.revokeObjectURL(url);
              }}
            >{VETSTAT_UI.datasetBuilder.exportCsv}</button>
            <button className="exportButton" type="button" disabled={selectedColumns.length === 0 && selectedYears.length === 0} onClick={() => setOpenExportDialog(true)} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>{VETSTAT_UI.datasetBuilder.exportImagePdf} <FileDownloadIcon fontSize="small" /></button>
            <ExportDialog open={openExportDialog} onClose={() => setOpenExportDialog(false)} onExport={(f) => f === 'pdf' ? handleExportPdf() : handleExportImage(f as any)} />
          </div>
        )}
        {!loading && !error && (
          <div ref={wrapperRef} className={`datasetTableWrapper ${layout !== 'original' ? 'a4-page' : ''}`}>
            <table ref={tableRef} className={`datasetTable ${rotateHeaders ? 'rotated-headers' : ''}`}>
              <thead>
                <tr><th></th>{sortedColumns.map((col) => (<th key={col}><div><span>{col}</span></div></th>))}</tr>
              </thead>
              <tbody>
                {groupedRows.map((group, groupIdx) => (
                  <React.Fragment key={group.species}>
                    <tr className="superRow">
                      <td>{group.species}</td>
                      {sortedColumns.map((col) => {
                        const val = group.totals[col] ?? 0;
                        return <td key={`total-${col}`}>{hideSubtotalValues ? "" : (val === 0 ? "-" : val.toFixed(precision))}</td>;
                      })}
                    </tr>
                    {group.midGroups.map((mid, midIdx) => (
                      <React.Fragment key={`${groupIdx}-${mid.midLabel}`}>
                        {isThreeLevel && mid.midLabel !== "" && (
                          <tr className="midRow" style={{ fontWeight: 'bold', backgroundColor: '#f9f9f9' }}>
                            <td style={{ paddingLeft: '1.5rem' }}>{mid.midLabel}</td>
                            {sortedColumns.map((col) => {
                              const val = mid.totals[col] ?? 0;
                              return <td key={`mid-total-${col}`}>{hideSubtotalValues ? "" : (val === 0 ? "-" : val.toFixed(precision))}</td>;
                            })}
                          </tr>
                        )}
                        {mid.subRows.map((row, rowIdx) => (
                          <tr key={`${groupIdx}-${midIdx}-${rowIdx}`} className={`subRow ${rowIdx % 2 !== 0 ? "alternating" : ""}`}>
                            <td style={{ paddingLeft: isThreeLevel ? '3rem' : '2rem' }}>
                                {isThreeLevel ? row.row_values[2] : row.row_values[1]}
                            </td>
                            {sortedColumns.map((col) => {
                              const val = row.cells[col] ?? 0;
                              return <td key={`cell-${col}`}>{val === 0 ? "-" : val.toFixed(precision)}</td>;
                            })}
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </React.Fragment>
                ))}
                {groupedRows.length === 0 && (
                  <tr><td colSpan={1 + sortedColumns.length}>{VETSTAT_UI.datasetBuilder.selectRowsAndColsToShow}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default PivotTable;